import React from "react";
import { ScalarKind } from "../types";
import StatusDropdown from "../Components/StatusDropdown";
import DateInput from "./DateInput";
import DropdownInput from "./DropdownInput";
import TextInput from "./TextInput";
import MultilineTextInput from "./MultilineTextInput";
import NumberlikeInput from "./NumberlikeInput";
import HierarchicalInput from "./HierarchicalInput";
import AddressInput from "./AddressInput";

interface ScalarInputProps {
  scalarType: ScalarKind | null;
  scalarUnit?: string | null;
  scalarValues?: any[] | null;
  value: any;
  onChange: (value: any) => void;
  // A number attribute's range end and note (#10991), so the editor opens
  // with the whole stored text.
  scalarEnd?: number | null;
  scalarNote?: string | null;
  isHierarchical?: boolean;
  placeholder?: string;
  className?: string;
  inputRef?: React.RefObject<
    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null
  >;
  compact?: boolean;
  // Forwarded to the boolean StatusDropdown so it reserves padding for
  // the leading Autofill chip / trailing Source chip. Other type-specific
  // inputs handle chip padding via their parent's arbitrary-variant
  // Tailwind selectors (`[&_input]:pl-24` etc.), so these only matter for
  // the boolean case which renders a <button>, not an <input>.
  hasAutofillChip?: boolean;
  hasSourceChip?: boolean;
}

const ScalarInput: React.FC<ScalarInputProps> = ({
  scalarType,
  scalarUnit,
  scalarValues,
  value,
  onChange,
  scalarEnd = null,
  scalarNote = null,
  isHierarchical = false,
  placeholder,
  className,
  inputRef,
  compact = false,
  hasAutofillChip = false,
  hasSourceChip = false,
}) => {
  // Hierarchical values take precedence
  if (isHierarchical) {
    return (
      <HierarchicalInput
        scalarType={scalarType}
        scalarUnit={scalarUnit}
        scalarValues={scalarValues}
        value={value}
        onChange={onChange}
        hasAutofillChip={hasAutofillChip}
        hasSourceChip={hasSourceChip}
      />
    );
  }

  // Enum/select values
  if (scalarValues && scalarValues.length > 0) {
    return (
      <DropdownInput
        value={value}
        onChange={onChange}
        options={scalarValues}
        className={className}
        inputRef={inputRef as React.RefObject<HTMLSelectElement>}
      />
    );
  }

  // Type-specific inputs
  switch (scalarType) {
    case "boolean":
      return (
        <StatusDropdown
          value={value === true ? "Yes" : value === false ? "No" : ""}
          onChange={(v) =>
            onChange(v === "Yes" ? true : v === "No" ? false : null)
          }
          options={["Yes", "No"]}
          hasAutofillChip={hasAutofillChip}
          hasSourceChip={hasSourceChip}
        />
      );
    case "date":
      return (
        <DateInput
          value={value}
          onChange={onChange}
          inputRef={inputRef as React.RefObject<HTMLInputElement>}
        />
      );
    case "number":
      // Every unit edits as text under the numberlike grammar (#10991): the
      // leading numbers are the value, the words after them are the note.
      return (
        <NumberlikeInput
          value={value}
          end={scalarEnd}
          note={scalarNote}
          unit={scalarUnit}
          onChange={onChange}
          placeholder={placeholder}
          className={className}
          inputRef={inputRef as React.RefObject<HTMLInputElement | null>}
          compact={compact}
        />
      );
    case "address":
      return (
        <AddressInput
          value={value}
          // An empty-shell AddressValue ({formatted: "", validated: false})
          // means the user cleared the field. The backend validator
          // rejects empty-formatted addresses, so translate that case to a
          // plain null — which the fact-write path treats as "clear this
          // fact" — before propagating upward.
          onChange={(v) =>
            onChange(
              v && typeof v.formatted === "string" && v.formatted.trim()
                ? v
                : null
            )
          }
          placeholder={placeholder}
          className={className}
          allowGeneral={scalarUnit !== "precise"}
          inputRef={inputRef as React.RefObject<HTMLInputElement | null>}
        />
      );
    case "string":
      if (scalarUnit === "multiline") {
        return (
          <MultilineTextInput
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            textareaRef={inputRef as React.RefObject<HTMLTextAreaElement>}
            className={className}
          />
        );
      }
      return (
        <TextInput
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={className}
          inputRef={inputRef as React.RefObject<HTMLInputElement>}
        />
      );
    default:
      return (
        <TextInput
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={className}
          inputRef={inputRef as React.RefObject<HTMLInputElement>}
        />
      );
  }
};

export default ScalarInput;
